import React, { useState } from "react";
import { EmptyState } from "@/components/ui/empty-state";
import {
  type ChannelMetrics,
  useChannelMetrics,
  useChannelStatuses,
} from "@/hooks/api/use-channel-metrics";
import { useChannelFilterState } from "@/hooks/use-channel-filter-state";
import { useChannelSelection } from "@/hooks/use-channel-selection";
import { useChannelStatusSelection } from "@/hooks/use-channel-status-selection";
import { useWipReportCardMetricsEnabled } from "@/hooks/use-feature-flags";
import {
  DEFAULT_METRIC_ORDER,
  DRILLDOWN_LIMIT,
  HIDDEN_METRICS,
  WIP_METRICS,
} from "./channel-metrics-config";
import { ChannelStatusSelector } from "./channel-status-selector";
import { ChannelMetricCards } from "./metric-cards";
import { MetricDrilldown } from "./metric-drilldown";

export function ChannelScorecard() {
  const { selectedChannelType } = useChannelSelection();
  const { selectedStatuses, auto, hasOverride } = useChannelStatusSelection(
    selectedChannelType || undefined,
  );
  const {
    accountFilters,
    opportunityFilters,
    touchpointFilters,
    campaignFilters,
    hasAccountFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
    hasCampaignFilters,
  } = useChannelFilterState("channels::explorer");

  const [selectedMetric, setSelectedMetric] = useState<
    keyof ChannelMetrics | null
  >(null);
  const wipMetricsEnabled = useWipReportCardMetricsEnabled();

  // Use DEFAULT_METRIC_ORDER and filter out WIP metrics if feature flag is disabled, then add required hidden metrics
  const metricsToFetch = React.useMemo(
    () => [
      ...new Set([
        ...(wipMetricsEnabled
          ? DEFAULT_METRIC_ORDER
          : DEFAULT_METRIC_ORDER.filter(
              (metric) => !WIP_METRICS.includes(metric),
            )),
        ...HIDDEN_METRICS,
      ]),
    ],
    [wipMetricsEnabled],
  );

  // Build filter object for API calls
  const apiFilters = React.useMemo(() => {
    return {
      ...(hasAccountFilters && { account_filters: accountFilters }),
      ...(hasOpportunityFilters && { opportunity_filters: opportunityFilters }),
      ...(hasTouchpointFilters && { touchpoint_filters: touchpointFilters }),
      ...(hasCampaignFilters && { campaign_filters: campaignFilters }),
    };
  }, [
    accountFilters,
    opportunityFilters,
    touchpointFilters,
    campaignFilters,
    hasAccountFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
    hasCampaignFilters,
  ]);

  // Fetch server defaults for statuses
  const { data: statusesData } = useChannelStatuses(
    selectedChannelType || undefined,
  );
  const defaultEngagementStatuses = React.useMemo(() => {
    const statuses = statusesData?.statuses || [];
    return statuses.filter((s) => s.represents_engagement).map((s) => s.status);
  }, [statusesData]);

  // Determine effective statuses
  const effectiveStatuses = React.useMemo(() => {
    // When auto is true, return empty array to use touchpoint-level engagement logic (IN_VALUE based)
    // This ensures Report Card matches List View which uses touchpoint.total_in_value > 0
    if (auto) return [];
    if (hasOverride) return selectedStatuses; // can be empty intentionally
    return defaultEngagementStatuses;
  }, [auto, hasOverride, selectedStatuses, defaultEngagementStatuses]);

  // Get channel metrics
  // When auto is true, we allow empty statuses (server will use IN_VALUE based logic)
  // When hasOverride is true, user must explicitly select at least one status
  const shouldShowMetrics =
    !!selectedChannelType && (auto || effectiveStatuses.length > 0);

  const { data: metrics, loadingStates } = useChannelMetrics({
    channelType: selectedChannelType || null,
    metrics: metricsToFetch,
    statuses: effectiveStatuses,
    filters: apiFilters,
    enabled: shouldShowMetrics,
  });

  return (
    <div className="space-y-4">
      <ChannelMetricCards
        metrics={metrics}
        onMetricSelect={(metric: string) =>
          setSelectedMetric(metric as keyof ChannelMetrics)
        }
        loadingStates={loadingStates}
        leftControls={
          selectedChannelType ? (
            <div className="flex flex-wrap items-center gap-2">
              <ChannelStatusSelector
                channelType={selectedChannelType || undefined}
              />
            </div>
          ) : null
        }
        showBody={shouldShowMetrics}
        emptyBody={
          !shouldShowMetrics ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-6">
              <EmptyState
                title="Select qualifying engagement statuses"
                description="Choose one or more statuses to see engagement metrics."
              />
            </div>
          ) : undefined
        }
      />

      {selectedMetric && (
        <MetricDrilldown
          open={selectedMetric !== null}
          onOpenChange={(open) => !open && setSelectedMetric(null)}
          channelType={selectedChannelType || null}
          metric={selectedMetric}
          statuses={effectiveStatuses}
          filters={apiFilters}
          metrics={metrics}
          loadingStates={loadingStates}
          drillDownLimit={DRILLDOWN_LIMIT}
        />
      )}
    </div>
  );
}
